/**
 * Process Landing Page
 *
 * Displays process-specific information and allows the citizen to start the submission.
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Description as ProcessIcon,
} from '@mui/icons-material';
import { getAuthorityExtendedInfo, getProcessesForAuthority } from '../api/citizenApi';

/**
 * Format fingerprint for display (groups of 4)
 */
function formatFingerprint(fingerprint: string): string {
  const groups: string[] = [];
  for (let i = 0; i < fingerprint.length; i += 4) {
    groups.push(fingerprint.slice(i, i + 4).toUpperCase());
  }
  return groups.join(' ');
}

export function ProcessLandingPage() {
  const { authorityKeyId, processKey } = useParams<{
    authorityKeyId: string;
    processKey: string;
  }>();
  const navigate = useNavigate();

  // Fetch authority extended info
  const {
    data: authority,
    isLoading: authorityLoading,
    error: authorityError,
  } = useQuery({
    queryKey: ['authority', authorityKeyId],
    queryFn: () => getAuthorityExtendedInfo(authorityKeyId!),
    enabled: !!authorityKeyId,
    retry: 1,
  });

  // Fetch processes to get the specific process info
  const {
    data: processes,
    isLoading: processesLoading,
  } = useQuery({
    queryKey: ['processes', authorityKeyId],
    queryFn: () => getProcessesForAuthority(authorityKeyId!),
    enabled: !!authorityKeyId && !!authority && !authority.isRevoked,
  });

  const process = processes?.find((p) => p.key === processKey);
  const isLoading = authorityLoading || processesLoading;

  // Redirect if key is revoked
  useEffect(() => {
    if (authority?.isRevoked) {
      // Key is revoked, show error state
    }
  }, [authority]);

  const handleStartApplication = () => {
    navigate(`/submit/${authorityKeyId}/${processKey}/form`, {
      state: {
        returnChannelEnabled: process?.returnChannelEnabled ?? true,
      },
    });
  };

  const handleBack = () => {
    navigate(`/submit/${authorityKeyId}`);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Informationen werden geladen...
        </Typography>
      </Box>
    );
  }

  if (authorityError || !authority) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Behörde nicht gefunden
          </Typography>
          <Typography>
            Der angegebene Link ist ungültig oder die Behörde ist nicht mehr registriert.
            Bitte wenden Sie sich an Ihre Behörde, um einen gültigen Link zu erhalten.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (authority.isRevoked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Schlüssel widerrufen
          </Typography>
          <Typography>
            Der Schlüssel dieser Behörde wurde widerrufen.
            <br />
            <br />
            Bitte wenden Sie sich an die Behörde, um einen aktuellen Link zu erhalten.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!process) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Prozess nicht gefunden
          </Typography>
          <Typography>
            Der angegebene Antrag existiert nicht oder ist nicht mehr verfügbar.
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Zur Übersicht
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Zur Übersicht
          </Button>

          {/* Authority Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <VerifiedIcon color="success" sx={{ fontSize: 32, mr: 1.5 }} />
            <Box>
              <Typography variant="subtitle1">
                {authority.organizationName || authority.ownerName}
              </Typography>
              <Chip
                label="Verifizierte Behörde"
                color="success"
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Process Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ProcessIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h5" component="h1">
                {process.name || process.key}
              </Typography>
              {process.description && (
                <Typography variant="body2" color="text.secondary">
                  {process.description}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Info */}
          <Typography variant="body1" color="text.secondary" paragraph>
            Sie sind dabei, einen verschlüsselten Antrag an diese Behörde zu senden.
            Ihre Daten werden Ende-zu-Ende verschlüsselt und können nur von der Behörde
            entschlüsselt werden.
          </Typography>

          {/* Security Info */}
          <Box
            sx={{
              bgcolor: 'success.light',
              borderRadius: 2,
              p: 2,
              mb: 3,
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <SecurityIcon sx={{ color: 'success.dark', mr: 1.5, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'success.dark' }}>
                Ende-zu-Ende-Verschlüsselung
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.dark' }}>
                Ihre Daten werden in Ihrem Browser verschlüsselt, bevor sie gesendet werden.
                Nur die Behörde kann sie entschlüsseln.
              </Typography>
            </Box>
          </Box>

          {/* Fingerprint */}
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Schlüssel-Fingerprint zur Verifikation:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              p: 1,
              borderRadius: 1,
              wordBreak: 'break-all',
              mb: 3,
            }}
          >
            {formatFingerprint(authority.fingerprint)}
          </Typography>

          {/* Start Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleStartApplication}
            endIcon={<ArrowForwardIcon />}
          >
            Antrag starten
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
